<template>
  <base-slide-over ref="businessPlanSlideover" :title="isNew === true ? 'New Plan' : 'Edit Plan'" size="2xl" style="z-index: 10000">
    <base-form ref="businessPlanForm" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid">
      <!-- Plan Information -->
      <div class="tw-text-gray-700 tw-text-lg tw-font-normal tw-mb-4">Plan Information</div>
      <base-text-field label="Plan Name" placeholder="Enter plan name" v-model="businessPlan.name" class="tw-mt-3" required :maxLength="255" />
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4">
        <base-select label="Plan Group" placeholder="Select a group" :options="planGroups" v-model="businessPlan.planGroup" />
        <base-text-field label="Sort Order" placeholder="Enter sort order" v-model="businessPlan.sortOrder" required type="number" min="0" step="1" />
      </div>
      <base-text-field
        v-if="businessPlan.planGroup === 'new group'"
        label="New Plan Group"
        placeholder="Enter new plan group"
        v-model="businessPlan.newPlanGroup"
        :rules="[() => (this.businessPlan.planGroup === 'new group' && this.businessPlan.newPlanGroup !== null) || 'Please enter a new group name']"
        :maxlength="255"
      />
      <base-text-field label="Short Description" placeholder="Enter a short description" v-model="businessPlan.descriptionShort" required :maxLength="255" />
      <base-textarea label="Long Description" placeholder="Enter a longer description" v-model="businessPlan.descriptionLong" required :maxLength="1020" />
      <base-text-field class="tw-mb-5" label="Tag" placeholder="Enter tag" v-model="businessPlan.tag" :maxLength="255" />
      <base-radio-group :value="businessPlan.status" class="tw-space-x-6 tw-flex" required>
        <base-radio value="active" name="status" v-model="businessPlan.status">Active</base-radio>
        <base-radio value="inactive" name="status" v-model="businessPlan.status">Inactive</base-radio>
        <base-radio value="archived" name="status" v-model="businessPlan.status">Archived</base-radio>
      </base-radio-group>
      <div class="tw-flex tw-mt-4">
        <div class="tw-text-sm tw-mr-2 tw-my-auto tw-text-gray-800 tw-cursor-pointer">Private Plan</div>
        <base-toggle v-model="businessPlan.isPrivate" />
      </div>
      <div class="tw-text-sm tw-mt-2 tw-text-gray-500">Private plans are only visible by business associates and do not show on member signup pages.</div>
      <!-- Plan Type -->
      <div class="tw-text-gray-700 tw-text-lg tw-font-normal tw-mt-8 tw-mb-3">Plan Type</div>
      <base-radio-group :value="businessPlan.planType" class="tw-space-x-6 tw-flex" required>
        <base-radio value="subscription" name="planType" v-model="businessPlan.planType">Subscription</base-radio>
        <base-radio value="countdown" name="planType" v-model="businessPlan.planType">Countdown</base-radio>
      </base-radio-group>
      <!-- Plan Cycle -->
      <div class="tw-text-gray-700 tw-text-lg tw-font-normal tw-mt-8 tw-mb-3">Plan Cycle</div>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4">
        <base-text-field label="Initial signup credits" v-model="initialQuantity" required type="number" min="0" step="1" />
        <base-text-field label="Credits each cylce" v-model="quantity" required type="number" min="0" step="1" />
      </div>
      <div class="tw-flex tw-text-sm tw-text-gray-500 tw-font-normal">
        You members will receive {{ sumInitialQuantity }} credit(s) upon initial payment. After each following payment cycle they will receive {{ quantity }} additional credit(s).
      </div>
      <base-advanced-select
        class="tw-my-5"
        label="Products"
        placeholder="Select Product(s)"
        :options="itemCategoryOptions"
        v-model="businessPlan.itemCategories"
        label-key="name"
        multiple
        :rules="[() => this.productCount > 0 || 'Please choose at least one product.']"
      />
      <div class="tw-flex">
        <base-text-field class="tw-w-1/4" label="Billed every" v-model="businessPlan.cycleFrequency" required type="number" min="1" step="1" :maxLength="2" />
        <base-radio-group :value="businessPlan.cycleInterval" class="tw-space-x-6 tw-flex tw-ml-4" required>
          <base-radio value="day" id="cycleDay" name="cycle" v-model="businessPlan.cycleInterval">Day</base-radio>
          <base-radio value="week" id="cycleWeek" name="cycle" v-model="businessPlan.cycleInterval">Week</base-radio>
          <base-radio value="month" id="cycleMonth" name="cycle" v-model="businessPlan.cycleInterval">Month</base-radio>
          <base-radio value="year" id="cycleYear" name="cycle" v-model="businessPlan.cycleInterval">Year</base-radio>
        </base-radio-group>
      </div>
      <div class="tw-flex">
        <base-currency-input class="tw-w-1/2" label="Price" :int-value.sync="businessPlan.priceCents" v-model="businessPlan.price" required />
        <div class="tw-flex tw-items-center tw-ml-4">
          <div class="tw-text-sm tw-mr-2 tw-text-gray-800 tw-cursor-pointer">Taxable</div>
          <base-toggle v-model="businessPlan.isTaxable" />
        </div>
      </div>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4">
        <base-currency-input label="Down Payment (added to initial subscription fee)" :int-value.sync="businessPlan.downPaymentCents" v-model="businessPlan.downPayment" required />
        <base-currency-input label="Service Fee (added each cycle)" :int-value.sync="businessPlan.serviceFeeCents" v-model="businessPlan.serviceFee" required />
      </div>
      <!-- Annual Savings -->
      <div class="tw-text-gray-700 tw-text-lg tw-font-normal tw-mt-6 tw-mb-3">Annual Savings</div>
      <div class="tw-flex tw-text-xs tw-text-gray-600 tw-font-normal">
        The annual savings presented to a customer at sign up is calculated based on the discount over the term of your subscription.
      </div>
      <div class="tw-flex tw-text-xs tw-text-gray-600 tw-font-normal tw-mt-2">
        If your plan relies on "Recharges" for ongoing revenue you can create a custom savings calculation to be presented to customers when signing up.
      </div>
      <div class="tw-flex tw-items-center tw-mt-2">
        <div class="tw-text-sm tw-mr-2 tw-text-gray-800 tw-cursor-pointer">Provide custom savings calculation</div>
        <base-toggle v-model="businessPlan.isCustomSavings" />
      </div>
      <div v-if="businessPlan.isCustomSavings" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4 tw-mt-3">
        <base-text-field label="Percent saved" v-model="businessPlan.savingsPercent" required type="number" min="0" step="1">
          <template v-slot:postfix>
            %
          </template>
        </base-text-field>
        <base-currency-input label="Annual Cost (basis for % saved)" :int-value.sync="businessPlan.savingsRevenue" v-model="businessPlan.displaySavingsRevenue" required />
      </div>
      <!-- Plan Renewal -->
      <div class="tw-text-gray-700 tw-text-lg tw-font-normal tw-mt-8 tw-mb-3">Plan Renewal</div>
      <div v-if="businessPlan.planType === 'subscription'" class="tw-flex tw-items-center tw-mt-3">
        <div class="tw-text-sm tw-mr-2 tw-text-gray-800 tw-cursor-pointer">Auto-renew plan</div>
        <base-toggle v-model="businessPlan.isAutoRenew" />
      </div>
      <div v-if="cycleEndValidation">
        <span class="tw-text-red-500 tw-text-sm">Please choose an end criteria for this plan.</span>
      </div>
      <div v-if="businessPlan.isAutoRenew" class="tw-text-gray-500 tw-text-sm tw-mt-1">
        Ongoing Membership: This plan will automatically renew until the member cancels the plan.
      </div>
      <div v-else>
        <div class="tw-text-gray-500 tw-text-sm tw-mt-1">
          This plan has a defined end. You can control how long the member makes payments and how long the plan will stay active.
        </div>
        <div class="tw-mt-4 tw-mb-2">
          <span class="tw-text-gray-800 tw-font-light">Plan Term:</span><span class="tw-text-gray-800 tw-font-light tw-text-sm tw-ml-2">(When do payments stop?)</span>
        </div>
        <base-list-group :listType="'unordered'">
          <base-list-item :class="{ active: cycleEndRadio === 'count' }">
            <div class="tw-flex">
              <base-radio value="count" class="tw-my-auto" id="cycleEndCountSetting" name="cycleEndRadio" v-model="cycleEndRadio"></base-radio>
              <span class="tw-text-gray-800 tw-my-auto tw-mr-2">After</span>
              <base-text-field
                class="tw-w-1/6"
                label=""
                v-model="businessPlan.cycleEndCount"
                :disabled="cycleEndRadio !== 'count'"
                :required="cycleEndRadio === 'count'"
                type="number"
                min="0"
                step="1"
              />
              <span class="tw-text-gray-800 tw-my-auto tw-ml-2">payment cycles</span>
            </div>
          </base-list-item>
          <base-list-item :class="{ active: cycleEndRadio === 'interval' }">
            <div class="tw-flex">
              <base-radio value="interval" class="tw-my-auto" id="cycleEndIntervalSetting" name="cycleEndRadio" v-model="cycleEndRadio"></base-radio>
              <span class="tw-text-gray-800 tw-my-auto tw-mr-2">After</span>
              <base-text-field
                class="tw-w-1/6"
                label=""
                v-model="businessPlan.cycleEndIntervalCount"
                :disabled="cycleEndRadio !== 'interval'"
                :required="cycleEndRadio === 'interval'"
                type="number"
                min="0"
                step="1"
              />
              <base-radio-group :value="businessPlan.cycleEndInterval" class="tw-space-x-6 tw-flex tw-ml-4" :required="cycleEndRadio === 'interval'">
                <base-radio value="day" id="cycleEndDay" name="cycleEndInterval" v-model="businessPlan.cycleEndInterval">Day</base-radio>
                <base-radio value="week" id="cycleEndWeek" name="cycleEndInterval" v-model="businessPlan.cycleEndInterval">Week</base-radio>
                <base-radio value="month" id="cycleEndMonth" name="cycleEndInterval" v-model="businessPlan.cycleEndInterval">Month</base-radio>
                <base-radio value="year" id="cycleEndYear" name="cycleEndInterval" v-model="businessPlan.cycleEndInterval">Year</base-radio>
              </base-radio-group>
            </div>
          </base-list-item>
        </base-list-group>

        <div class="tw-mt-6 tw-mb-2">
          <span class="tw-text-gray-800 tw-font-light">Plan Length:</span><span class="tw-text-gray-800 tw-font-light tw-text-sm tw-ml-2">(When does the plan get cancelled?)</span>
        </div>
        <base-list-group :listType="'unordered'">
          <base-list-item :class="{ active: planCancelRadio === 'count' }">
            <div class="tw-flex">
              <base-radio value="count" class="tw-my-auto" id="planCancelCountSetting" name="planCancelRadio" v-model="planCancelRadio"></base-radio>
              <span class="tw-text-gray-800 tw-my-auto tw-mr-2">After</span>
              <base-text-field
                class="tw-w-1/6"
                label=""
                v-model="businessPlan.cancelAfterCycleCount"
                :disabled="planCancelRadio !== 'count'"
                :required="planCancelRadio === 'count'"
                type="number"
                min="0"
                step="1"
              />
              <span class="tw-text-gray-800 tw-my-auto tw-ml-2">payment cycles</span>
            </div>
          </base-list-item>
          <base-list-item :class="{ active: planCancelRadio === 'interval' }">
            <div class="tw-flex">
              <base-radio value="interval" class="tw-my-auto" id="planCancelIntervalSetting" name="planCancelRadio" v-model="planCancelRadio"></base-radio>
              <span class="tw-text-gray-800 tw-my-auto tw-mr-2">After</span>
              <base-text-field
                class="tw-w-1/6"
                label=""
                v-model="businessPlan.cancelAfterIntervalCount"
                :disabled="planCancelRadio !== 'interval'"
                :required="planCancelRadio === 'interval'"
                type="number"
                min="0"
                step="1"
              />
              <base-radio-group :value="businessPlan.cancelAfterInterval" class="tw-space-x-6 tw-flex tw-ml-4" :required="planCancelRadio === 'interval'">
                <base-radio value="day" id="planCancelDay" name="planCancelInterval" v-model="businessPlan.cancelAfterInterval">Day</base-radio>
                <base-radio value="week" id="planCancelWeek" name="planCancelInterval" v-model="businessPlan.cancelAfterInterval">Week</base-radio>
                <base-radio value="month" id="planCancelMonth" name="planCancelInterval" v-model="businessPlan.cancelAfterInterval">Month</base-radio>
                <base-radio value="year" id="planCancelYear" name="planCancelInterval" v-model="businessPlan.cancelAfterInterval">Year</base-radio>
              </base-radio-group>
            </div>
          </base-list-item>
        </base-list-group>
      </div>
      <div v-if="businessPlan.isAutoRenew" class="tw-mt-4">
        <div class="tw-text-gray-800 tw-text-sm tw-font-normal">Plan Term</div>
        <div class="tw-text-gray-500 tw-text-sm tw-mb-2">This is the minimum length of time the member is agreeing to be on your plan.</div>
        <div class="tw-flex">
          <base-text-field
            class="tw-w-1/5"
            label="Term Length"
            v-model="businessPlan.termLength"
            :disabled="businessPlan.isAutoRenew === false"
            :required="businessPlan.isAutoRenew === true"
            type="number"
            min="0"
            step="1"
          />
          <base-radio-group :value="businessPlan.termInterval" class="tw-space-x-6 tw-flex tw-ml-4" :required="businessPlan.isAutoRenew === true">
            <base-radio value="day" id="termDay" name="term" v-model="businessPlan.termInterval">Day</base-radio>
            <base-radio value="week" id="termWeek" name="term" v-model="businessPlan.termInterval">Week</base-radio>
            <base-radio value="month" id="termMonth" name="term" v-model="businessPlan.termInterval">Month</base-radio>
            <base-radio value="year" id="termYear" name="term" v-model="businessPlan.termInterval">Year</base-radio>
          </base-radio-group>
        </div>
      </div>
    </base-form>
    <inline-error id="business-plan-error" :title="errorTitle" :message="errorMessage" :error="error" />
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-3" @click="close()">
          Cancel
        </base-button>
        <base-button :loading="isSaving" :disabled="!isFormValid" class="tw-w-auto" @click="save">
          Save
        </base-button>
      </div>
    </template>
  </base-slide-over>
</template>
<script>
import { mapActions } from "vuex";
import InlineError from "@/components/InlineError";

export default {
  name: "BusinessPlanSlideover",
  components: {
    InlineError,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      isSaving: false,
      isFormValid: true,
      isNew: false,
      businessPlan: {
        name: null,
        planGroup: null,
        newPlanGroup: null,
        sortOrder: 1,
        descriptionShort: null,
        descriptionLong: null,
        tag: null,
        price: 0,
        priceCents: 0,
        priceCurrency: "USD",
        downPayment: 0,
        downPaymentCents: 0,
        serviceFee: 0,
        serviceFeeCents: 0,
        isCustomSavings: false,
        savingsPercent: 0,
        savingsRevenue: 0,
        displaySavingsRevenue: 0,
        startDate: null,
        stopDate: null,
        itemCategories: [],
        isPrivate: false,
        cycleFrequency: 1,
        cycleInterval: "month",
        termInterval: "month",
        termLength: 1,
        isAutoRenew: true,
        isTaxable: false,
        cycleEndCount: 0,
        cycleEndIntervalCount: 0,
        cycleEndInterval: null,
        cancelAfterCycleCount: 0,
        cancelAfterIntervalCount: 0,
        cancelAfterInterval: null,
        planType: "subscription",
        status: "active",
      },
      planGroups: [],
      errorTitle: "",
      errorMessage: "",
      error: null,
      quantity: 1,
      initialQuantity: 0,
      itemCategoryOptions: [],

      cycleEndRadio: null,
      cycleEndValidation: false,

      planCancelRadio: null,
      planCancelValidation: false,
    };
  },
  computed: {
    computedPlanType() {
      return this.businessPlan.planType;
    },
    sumInitialQuantity() {
      return Number(this.initialQuantity) + Number(this.quantity);
    },
    productCount() {
      return this.businessPlan.itemCategories ? this.businessPlan.itemCategories.length : 0;
    },
  },
  watch: {
    computedPlanType: {
      handler() {
        if (this.businessPlan.planType === "countdown") {
          this.businessPlan.isAutoRenew = false;
          this.initialQuantity = 1;
          this.quantity = 0;
        } else {
          this.businessPlan.isAutoRenew = true;
          this.initialQuantity = 0;
          this.quantity = 1;
        }
      },
    },
    businessPlan: {
      handler: function(newPlan) {
        if (newPlan.isAutoRenew === true) {
          this.businessPlan.cycleEndCount = null;
          this.businessPlan.cycleEndIntervalCount = null;
          this.businessPlan.cycleEndInterval = null;
          this.cycleEndRadio = null;
          this.cycleEndValidation = false;

          this.businessPlan.cancelAfterCycleCount = null;
          this.businessPlan.cancelAfterIntervalCount = null;
          this.businessPlan.cancelAfterInterval = null;
          this.planCancelRadio = null;
          this.planCancelValidation = false;
        } else {
          this.businessPlan.termInterval = "month";
          this.businessPlan.termLength = 1;
        }
      },
      deep: true,
    },
    cycleEndRadio: {
      handler: function(newRadio) {
        if (newRadio === "count") {
          this.businessPlan.cycleEndIntervalCount = null;
          this.businessPlan.cycleEndInterval = null;
          this.cycleEndValidation = false;
        } else {
          this.businessPlan.cycleEndCount = null;
          this.cycleEndValidation = false;
        }
      },
    },
    planCancelRadio: {
      handler: function(newRadio) {
        if (newRadio === "count") {
          this.businessPlan.cancelAfterIntervalCount = null;
          this.businessPlan.cancelAfterInterval = null;
          this.planCancelValidation = false;
        } else {
          this.businessPlan.cancelAfterCycleCount = null;
          this.planCancelValidation = false;
        }
      },
    },
  },
  created() {
    this.getItemCategories();
  },
  methods: {
    ...mapActions("businessActivation", { businessActivationInit: "init" }),
    reset() {
      this.isSaving = false;
      this.isNew = false;
      this.businessPlan = {
        name: null,
        planGroup: null,
        newPlanGroup: null,
        sortOrder: 1,
        descriptionShort: null,
        descriptionLong: null,
        tag: null,
        price: 0,
        priceCents: 0,
        priceCurrency: "USD",
        downPayment: 0,
        downPaymentCents: 0,
        serviceFee: 0,
        serviceFeeCents: 0,
        isCustomSavings: false,
        savingsPercent: 0,
        savingsRevenue: 0,
        displaySavingsRevenue: 0,
        startDate: null,
        stopDate: null,
        itemCategories: [],
        isPrivate: false,
        isTaxable: false,
        cycleFrequency: 1,
        cycleInterval: "month",
        termInterval: "month",
        termLength: 1,
        isAutoRenew: true,
        cycleEndCount: 0,
        cycleEndIntervalCount: 0,
        cycleEndInterval: null,
        cancelAfterCycleCount: 0,
        cancelAfterIntervalCount: 0,
        cancelAfterInterval: null,
        planType: "subscription",
        status: "active",
      };
      this.cycleEndRadio = null;
      this.cycleEndValidation = false;

      this.planCancelRadio = null;
      this.planCancelValidation = false;

      this.quantity = 1;
      this.initialQuantity = 0;
      this.planGroups = [];
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
    },
    show(businessPlan, planGroups) {
      this.reset();
      if (!businessPlan) {
        this.isNew = true;
        this.initializeStartDate();
        this.initializeStopDate();
      } else this.syncBusinessPlan(businessPlan);

      this.planGroups = planGroups;
      this.$refs.businessPlanSlideover.show();
    },
    getItemCategories() {
      const url = `/businesses/${this.businessId}/item-categories/all`;

      this.$axios.get(url).then((res) => {
        this.itemCategoryOptions = res.data.filter((a) => a.type === "product").sort((a, b) => (a.name > b.name ? 1 : -1));
      });
    },
    initializeStartDate(startDate) {
      if (!startDate) {
        this.businessPlan.startDate = new Date().toISOString();
      } else {
        this.businessPlan.startDate = new Date(Date.parse(startDate + "Z")).toISOString();
      }
    },
    initializeStopDate(stopDate) {
      if (stopDate == null) {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth();
        const day = today.getDate();

        this.businessPlan.stopDate = new Date(year + 1, month, day).toISOString();
      } else {
        this.businessPlan.stopDate = new Date(Date.parse(stopDate + "Z")).toISOString();
      }
    },
    initializePlanEnd(cycleEndCount, cycleEndIntervalCount, cycleEndInterval, cancelAfterCycleCount, cancelAfterIntervalCount, cancelAfterInterval) {
      if (cycleEndCount) this.cycleEndRadio = "count";
      else if (cycleEndIntervalCount || cycleEndInterval) this.cycleEndRadio = "interval";

      if (cancelAfterCycleCount) this.planCancelRadio = "count";
      else if (cancelAfterIntervalCount || cancelAfterInterval) this.planCancelRadio = "interval";
    },
    close() {
      this.$refs.businessPlanSlideover.hide();
    },
    save() {
      if (!this.$refs.businessPlanForm.validate()) return;

      this.isSaving = true;

      if (this.businessPlan.planGroup === "new group" && this.businessPlan.newPlanGroup !== null) this.businessPlan.planGroup = this.businessPlan.newPlanGroup;

      this.cycleEndValidation = false;
      if (this.businessPlan.isAutoRenew === false && (this.cycleEndRadio === null || this.planCancelRadio === null)) {
        this.cycleEndValidation = true;
        this.isSaving = false;
        return;
      }

      for (const item of this.businessPlan.itemCategories) {
        item.quantity = this.quantity;
        item.initialQuantity = this.initialQuantity;
      }

      if (this.isNew) {
        this.$axios
          .post(`/businesses/${this.businessId}/plans`, {
            ...this.businessPlan,
          })
          .then(() => {
            this.close();
            this.$emit("save");
            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Plan Saved", message: this.businessPlan.name + " was created successfully" });
            this.businessActivationInit();
          })
          .catch((err) => {
            this.errorTitle = `Failed to create ${this.businessPlan.name}`;
            this.errorMessage = `Business plan ${this.businessPlan.name} was not able to be created. Please try again.`;
            this.error = err;
          })
          .finally(() => {
            this.isSaving = false;
          });
      } else {
        this.$axios
          .put(`/businesses/${this.businessId}/plans/${this.businessPlan.businessPlanId}`, {
            ...this.businessPlan,
          })
          .then(() => {
            this.close();
            this.$emit("save");
            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Plan Updated", message: this.businessPlan.name + " was modified successfully" });
            this.businessActivationInit();
          })
          .catch((err) => {
            this.errorTitle = `Failed to modify ${this.businessPlan.name}`;
            this.errorMessage = `Business plan ${this.businessPlan.name} was not able to be modified. Please try again.`;
            this.error = err;
          })
          .finally(() => {
            this.isSaving = false;
          });
      }
    },
    syncBusinessPlan(businessPlan) {
      this.businessPlan.businessPlanId = businessPlan.businessPlanId;
      this.businessPlan.name = businessPlan.name;
      this.businessPlan.planGroup = businessPlan.planGroup;
      this.businessPlan.sortOrder = businessPlan.sortOrder;
      this.businessPlan.descriptionShort = businessPlan.descriptionShort;
      this.businessPlan.descriptionLong = businessPlan.descriptionLong;
      this.businessPlan.tag = businessPlan.tag;
      this.businessPlan.price = businessPlan.priceCents;
      this.businessPlan.priceCurrency = businessPlan.currency;
      this.businessPlan.downPayment = businessPlan.downPaymentCents;
      this.businessPlan.serviceFee = businessPlan.serviceFeeCents;
      this.businessPlan.isCustomSavings = businessPlan.isCustomSavings;
      this.businessPlan.savingsPercent = businessPlan.savingsPercent;
      this.businessPlan.displaySavingsRevenue = businessPlan.savingsRevenue;
      this.businessPlan.startDate = businessPlan.startDate;
      this.businessPlan.stopDate = businessPlan.stopDate;
      this.businessPlan.itemCategories = businessPlan.itemCategories;
      this.businessPlan.isPrivate = businessPlan.isPrivate;
      this.businessPlan.isTaxable = businessPlan.isTaxable;
      this.businessPlan.cycleFrequency = businessPlan.billingFrequency;
      this.businessPlan.cycleInterval = businessPlan.billingInterval;
      this.businessPlan.termInterval = businessPlan.termInterval;
      this.businessPlan.termLength = businessPlan.termLength;
      this.businessPlan.isAutoRenew = businessPlan.isAutoRenew;
      this.businessPlan.cycleEndCount = businessPlan.cycleEndCount;
      this.businessPlan.cycleEndIntervalCount = businessPlan.cycleEndIntervalCount;
      this.businessPlan.cycleEndInterval = businessPlan.cycleEndInterval;
      this.businessPlan.cancelAfterCycleCount = businessPlan.cancelAfterCycleCount;
      this.businessPlan.cancelAfterIntervalCount = businessPlan.cancelAfterIntervalCount;
      this.businessPlan.cancelAfterInterval = businessPlan.cancelAfterInterval;
      this.businessPlan.planType = businessPlan.planType;
      this.businessPlan.status = businessPlan.status;

      this.quantity = businessPlan.itemCategories[0].quantity;
      this.initialQuantity = businessPlan.itemCategories[0].initialQuantity;

      this.initializePlanEnd(
        businessPlan.cycleEndCount,
        businessPlan.cycleEndIntervalCount,
        businessPlan.cycleEndInterval,
        businessPlan.cancelAfterCycleCount,
        businessPlan.cancelAfterIntervalCount,
        businessPlan.cancelAfterInterval,
      );
    },
  },
};
</script>
